import { API } from '../api.js';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import styles from './AdminDashboardTab.module.css';

export default function AdminDashboardTab({ onToast }) {
  const { token } = useAuth();
  const { t } = useLang();
  
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [health, setHealth] = useState(null);
  const [pingTime, setPingTime] = useState(null);

  async function loadData() {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const startPing = Date.now();
      
      const [uRes, commRes, courseRes, planRes, subRes, healthRes] = await Promise.all([
        fetch(`${API}/api/users`, { headers }),
        fetch(`${API}/api/communities`, { headers }),
        fetch(`${API}/api/courses`, { headers }),
        fetch(`${API}/api/plans/all`, { headers }),
        fetch(`${API}/api/subscriptions`, { headers }),
        fetch(`${API}/api/health`, { headers }).catch(() => null)
      ]);

      const endPing = Date.now();
      setPingTime(endPing - startPing);

      if (uRes.ok) setUsers(await uRes.json());
      if (commRes.ok) setCommunities(await commRes.json());
      if (courseRes.ok) setCourses(await courseRes.json());
      if (planRes.ok) setPlans(await planRes.json());
      if (subRes.ok) setSubscriptions(await subRes.json());
      
      if (healthRes && healthRes.ok) {
        setHealth(await healthRes.json());
      } else {
        setHealth({ status: 'offline' });
      }
    } catch (e) {
      console.error(e);
      onToast(t('common.error') || 'Failed to load system metrics');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>{t('common.loading')}</div>;
  }

  // Calculate MRR
  const activeSubs = subscriptions.filter(s => s.status === 'active');
  const mrr = plans.reduce((acc, p) => {
    const count = activeSubs.filter(s => s.plan_id === p.id).length;
    return acc + count * p.price_monthly;
  }, 0);

  // Recent Users
  const recentUsers = users.slice(0, 5);

  return (
    <div className="flex-col">
      {/* Overview Cards */}
      <div className={styles.statsGrid}>
        <div className={`${styles.card} ${styles.cardPurple}`}>
          <div className={styles.headerRow}>
            <span className={styles.label}>Total Users</span>
            <div className={`${styles.iconWrapper} ${styles.purpleIcon}`}>👥</div>
          </div>
          <div className={styles.value}>{users.length}</div>
          <div className={styles.change}>{users.filter(u => u.is_active).length} active accounts</div>
        </div>

        <div className={`${styles.card} ${styles.cardGreen}`}>
          <div className={styles.headerRow}>
            <span className={styles.label}>MRR</span>
            <div className={`${styles.iconWrapper} ${styles.greenIcon}`}>💰</div>
          </div>
          <div className={styles.value}>฿{mrr.toLocaleString()}</div>
          <div className={styles.change}>Projected ARR: ฿{(mrr * 12).toLocaleString()}</div>
        </div>

        <div className={`${styles.card} ${styles.cardOrange}`}>
          <div className={styles.headerRow}>
            <span className={styles.label}>Communities</span>
            <div className={`${styles.iconWrapper} ${styles.orangeIcon}`}>🏫</div>
          </div>
          <div className={styles.value}>{communities.length}</div>
          <div className={styles.change}>{communities.filter(c => c.pricing !== 'free').length} premium portals</div>
        </div>

        <div className={`${styles.card} ${styles.cardRed}`}>
          <div className={styles.headerRow}>
            <span className={styles.label}>Total Courses</span>
            <div className={`${styles.iconWrapper} ${styles.redIcon}`}>📚</div>
          </div>
          <div className={styles.value}>{courses.length}</div>
          <div className={styles.change}>{courses.filter(c => c.isPublished).length} published</div>
        </div>
      </div>

      {/* Server Health Status */}
      <h3 style={{ margin: '8px 0 16px 0', fontSize: 15, color: 'var(--text)' }}>System Health Status</h3>
      <div className={styles.healthGrid}>
        <div className={styles.healthCard}>
          <div className={`${styles.healthIndicator} ${health?.status !== 'ok' ? styles.err : ''}`} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>Serverpod Core Engine</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
              Status: <span style={{ color: health?.status === 'ok' ? 'var(--accent3)' : 'var(--accent2)', fontWeight: 600 }}>{health?.status === 'ok' ? 'ONLINE (Healthy)' : 'OFFLINE / UNHEALTHY'}</span>
            </div>
          </div>
        </div>
        <div className={styles.healthCard}>
          <div className="stat-icon" style={{ fontSize: 20 }}>⚡</div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>API Response Latency</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
              Ping time: <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{pingTime}ms</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Signups */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="card-title">🆕 Recent User Registrations</div>
          <button className="btn btn-ghost btn-sm" onClick={loadData}>🔄 Refresh</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Subscribed Plan</th>
                <th>Status</th>
                <th>Registered Date</th>
              </tr>
            </thead>
            <tbody>
              {recentUsers.map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <img 
                        src={u.avatar_url ? `${API}${u.avatar_url}` : `https://api.dicebear.com/7.x/initials/svg?seed=${u.display_name}`} 
                        alt="avatar" 
                        style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', background: 'var(--surface2)' }} 
                      />
                      <div>
                        <div style={{ fontWeight: 500, color: 'var(--text)' }}>{u.display_name}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span style={{ 
                      fontSize: 11, 
                      fontWeight: 600, 
                      padding: '2px 8px', 
                      borderRadius: 12,
                      background: u.role === 'admin' ? 'rgba(255, 107, 107, 0.1)' : 'rgba(108, 99, 255, 0.1)',
                      color: u.role === 'admin' ? 'var(--accent2)' : 'var(--accent)'
                    }}>
                      {u.role.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <span className={styles.recentBadge}>{u.plan_name || 'Free'}</span>
                  </td>
                  <td>
                    <span style={{ 
                      fontWeight: 600, 
                      color: u.is_active ? 'var(--accent3)' : 'var(--text3)', 
                      fontSize: 12 
                    }}>
                      {u.is_active ? '● Active' : '○ Banned'}
                    </span>
                  </td>
                  <td>{new Date(u.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {recentUsers.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>
                    No registered users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
